import { type ReactNode } from "react";

import { t } from "i18next";

import { Box, Button, SxProps, Theme, Typography } from "@mui/material";

import { isDarkMode, primaryContainedButtonSx } from "@/src/theme/helpers";

import { ArticleBodyContent, btoaUTF8 } from "@/src/utils/blogUtils";
import { parseVideoEmbedUrl } from "@/src/utils/videoEmbedUtils";

import ArticleExternalTextLink, {
  getArticleOpensInNewTabAriaLabel
} from "./ArticleExternalTextLink";
import ArticleListItemContent from "./ArticleListItemContent";

const BODY_ITEM_SPACING = { mb: 1.5 };
const PARAGRAPH_SPACING = { mb: 1 };

const articleExternalButtonRowSx: SxProps<Theme> = {
  mb: 3,
  mt: 2,
  width: "fit-content",
  maxWidth: "100%"
};

const articleExternalButtonSx: SxProps<Theme> = (muiTheme) => ({
  ...primaryContainedButtonSx(muiTheme),
  width: "fit-content",
  maxWidth: "100%",
  minWidth: "unset",
  boxShadow: 2,
  px: 2.5,
  py: 1.25,
  textTransform: "uppercase",
  fontWeight: muiTheme.typography.fontWeightBold,
  "&.MuiButton-outlined.MuiButton-colorPrimary:hover": {
    backgroundColor: isDarkMode(muiTheme)
      ? muiTheme.palette.primary.dark
      : muiTheme.palette.misc.coldBackground,
    boxShadow: muiTheme.shadows[2]
  }
});

export interface ArticleBodyItemRenderCtaParams {
  text: string;
  bodyIndex: number;
  /** From CMS CTA; drives car / moto / caravan navigation when set. */
  href?: string;
  legacyGtmSlot?: "afterIngress" | "afterBody";
}

interface ArticleBodyItemProps {
  data: ArticleBodyContent;
  isLast?: boolean;
  bodyIndex: number;
  renderArticleCtaButton?: (
    params: ArticleBodyItemRenderCtaParams
  ) => ReactNode;
}

export const ArticleBodyItem = (props: ArticleBodyItemProps) => {
  switch (props.data.kind) {
    case "ArticleParagraph":
      return (
        <Box component="div" sx={props.isLast ? { mb: 1 } : PARAGRAPH_SPACING}>
          {props.data.contentItems.map((item, idx) => {
            if (item.kind === "ArticleLink") {
              return (
                <ArticleExternalTextLink
                  key={`article-paragraph-link-${idx}`}
                  url={item.url}
                  text={item.text}
                />
              );
            } else if (item.kind === "ArticleParagraphText") {
              return (
                <Typography
                  component="p"
                  variant="body1"
                  key={`article-paragraph-text-${idx}`}
                  sx={{
                    display: "inline"
                  }}
                >
                  {item.value}
                </Typography>
              );
            } else {
              console.log("Unknown ArticleParagraphContent kind");
              return null;
            }
          })}
        </Box>
      );

    case "ArticleImage":
      return (
        <Box
          component="figure"
          sx={{
            display: "flex",
            flexDirection: "column",
            margin: 0,
            width: "100%",
            mt: 2,
            mb: 0
          }}
        >
          <Box
            component="div"
            sx={{
              width: "100%",
              aspectRatio: "16 / 9",
              minHeight: "200px",
              overflow: "hidden"
            }}
          >
            <img
              src={props.data.url}
              alt={props.data.caption}
              width={800}
              height={450}
              loading="lazy"
              decoding="async"
              style={{
                width: "100%",
                height: "100%",
                margin: "0 auto",
                aspectRatio: "16 / 9",
                objectFit: "contain"
              }}
            />
          </Box>
          {props.data.caption && (
            <Box
              component="figcaption"
              sx={(muiTheme) => ({
                pt: 1,
                fontSize: muiTheme.typography.body2.fontSize,
                color: muiTheme.palette.text.secondary,
                textAlign: "start",
                mb: 1.5
              })}
            >
              {props.data.caption}
            </Box>
          )}
        </Box>
      );

    case "ArticleSubheading":
      return (
        <Box component="div">
          <Typography component="h2" variant="h2" sx={{ pb: 0, mb: 1, mt: 1 }}>
            {props.data.value}
          </Typography>
        </Box>
      );

    case "ArticleBulletedList":
      return (
        <Box component="div" sx={BODY_ITEM_SPACING}>
          <Box
            component="ul"
            sx={{ listStyleType: "disc", pl: 2.5, "& li": { mb: 1 } }}
          >
            {props.data.itemList.map((item, idx) => (
              <li key={`article-bulleted-list-item-${idx}`}>
                <ArticleListItemContent
                  item={item}
                  itemKey={`article-bulleted-list-item-${idx}`}
                />
              </li>
            ))}
          </Box>
        </Box>
      );

    case "ArticleNumberedList":
      return (
        <Box component="div" sx={BODY_ITEM_SPACING}>
          <Box component="ol" sx={{ pl: 2.5, "& li": { mb: 1 } }}>
            {props.data.itemList.map((item, idx) => (
              <li key={`article-numbered-list-item-${idx}`}>
                <ArticleListItemContent
                  item={item}
                  itemKey={`article-numbered-list-item-${idx}`}
                />
              </li>
            ))}
          </Box>
        </Box>
      );

    case "ArticleLink":
      return (
        <Box component="div" sx={PARAGRAPH_SPACING}>
          <ArticleExternalTextLink
            url={props.data.url}
            text={props.data.text}
          />
        </Box>
      );

    case "ArticleExternalButton":
      if (props.data.text.trim() === "") {
        return null;
      }
      return (
        <Box sx={articleExternalButtonRowSx}>
          <Button
            component="a"
            href={props.data.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={getArticleOpensInNewTabAriaLabel(props.data.text)}
            variant="contained"
            color="primary"
            size="small"
            sx={articleExternalButtonSx}
          >
            {props.data.text}
          </Button>
        </Box>
      );

    case "ArticleParagraphTextItalic":
      return (
        <Box component="div" sx={PARAGRAPH_SPACING}>
          <Typography
            component="p"
            variant="body1"
            sx={{
              fontStyle: "italic"
            }}
          >
            {props.data.value}
          </Typography>
        </Box>
      );

    case "ArticleVideo": {
      const embedResult = parseVideoEmbedUrl(props.data.url);
      if (!embedResult) {
        if (import.meta.env.DEV) {
          console.warn(
            "ArticleVideo: Unsupported video URL, rendering fallback link:",
            props.data.url
          );
        }
        return (
          <Box component="div" sx={BODY_ITEM_SPACING}>
            <ArticleExternalTextLink
              url={props.data.url}
              text={t("components.articlePage.watchVideo")}
            />
          </Box>
        );
      }

      return (
        <Box
          component="figure"
          sx={{
            display: "flex",
            flexDirection: "column",
            m: 0,
            ...BODY_ITEM_SPACING,
            width: "100%"
          }}
        >
          <Box
            sx={{
              width: "100%",
              aspectRatio: "16 / 9",
              minHeight: "200px",
              position: "relative",
              overflow: "hidden",
              "& iframe": {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%"
              }
            }}
          >
            <iframe
              src={embedResult.embedUrl}
              title={`${embedResult.provider} video`}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{
                width: "100%",
                height: "100%",
                aspectRatio: "16 / 9",
                border: 0
              }}
            />
          </Box>
          {props.data.caption && (
            <Box
              component="figcaption"
              sx={(muiTheme) => ({
                pt: 1,
                fontSize: muiTheme.typography.body2.fontSize,
                color: muiTheme.palette.text.secondary,
                textAlign: "start",
                mb: 1.5
              })}
            >
              {props.data.caption}
            </Box>
          )}
        </Box>
      );
    }

    case "ArticleCtaButton": {
      if (props.data.text.trim() === "") {
        return null;
      }
      return (
        <>
          {props.renderArticleCtaButton?.({
            text: props.data.text,
            bodyIndex: props.bodyIndex,
            href: props.data.href,
            legacyGtmSlot: props.data.legacyGtmSlot
          }) ?? null}
        </>
      );
    }

    case "ArticleTable":
      return (
        <Box
          sx={(muiTheme) => ({
            ...BODY_ITEM_SPACING,
            width: "100%",
            overflowX: "auto",
            border: `1px solid ${muiTheme.palette.divider}`,
            my: 1.5,
            table: {
              width: "100%",
              borderCollapse: "collapse"
            },
            th: {
              textAlign: "left",
              px: 1,
              borderRight: `1px solid ${muiTheme.palette.divider}`,
              borderBottom: `1px solid ${muiTheme.palette.divider}`,
              "&:last-child": {
                borderRight: "none"
              }
            },
            td: {
              textAlign: "left",
              px: 1,

              borderRight: `1px solid ${muiTheme.palette.divider}`,
              "&:last-child": {
                borderRight: "none"
              }
            }
          })}
        >
          <table>
            <thead>
              <tr>
                {props.data.columnHeaders.map((item, idx) => (
                  <th key={`table-column-header-${idx}-${btoaUTF8(item)}`}>
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {props.data.rows.map((row, rowIdx) => (
                <tr
                  key={`table-row-${rowIdx}-${btoaUTF8(JSON.stringify(row))}`}
                >
                  {row.map((item, idx) => {
                    const cellValue = String(item);
                    return (
                      <td
                        key={`table-row-${rowIdx}-item-${idx}-${btoaUTF8(
                          cellValue
                        )}`}
                      >
                        {cellValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );

    default:
      console.log("Unknown ArticleBodyContent kind");
      return null;
  }
};
